import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * 
 * @param htmlContent - The HTML content to sanitize
 * @param allowedTags - Optional array of additional allowed tags
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(htmlContent: string, allowedTags?: string[]): string {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return '';
  }

  // Default configuration for email templates and rich content
  const defaultConfig = {
    // Allow common formatting tags for emails
    ALLOWED_TAGS: [
      'p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'blockquote', 'pre', 'code',
      'hr', 'small', 'sub', 'sup'
    ],
    
    // Allow safe attributes
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height',
      'style', 'class', 'id',
      'colspan', 'rowspan',
      'target', 'rel'
    ],
    
    // Allow safe protocols for links and images
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    
    // Remove unsafe elements
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'select', 'textarea', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    
    // Add additional security
    WHOLE_DOCUMENT: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  };

  // Merge additional allowed tags if provided
  if (allowedTags && allowedTags.length > 0) {
    defaultConfig.ALLOWED_TAGS = [...defaultConfig.ALLOWED_TAGS, ...allowedTags];
  }

  try {
    // Sanitize the HTML content
    const sanitized = DOMPurify.sanitize(htmlContent, defaultConfig);
    
    // Additional safety: remove any remaining script tags that might have been missed
    return sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  } catch (error) {
    console.error('Error sanitizing HTML content:', error);
    // Return empty string if sanitization fails
    return '';
  }
}

/**
 * Sanitizes email template content specifically for email previews
 * Uses stricter rules appropriate for email content
 */
export function sanitizeEmailContent(emailHtml: string): string {
  if (!emailHtml || typeof emailHtml !== 'string') {
    return '';
  }

  const emailConfig = {
    ALLOWED_TAGS: [
      'p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'blockquote', 'hr', 'small'
    ],
    
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'width', 'height',
      'style', 'class',
      'colspan', 'rowspan',
      'target'
    ],
    
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    
    // Stricter for emails
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'select', 'textarea', 'button', 'iframe', 'frame'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onmouseout'],
    
    WHOLE_DOCUMENT: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false
  };

  try {
    const sanitized = DOMPurify.sanitize(emailHtml, emailConfig);
    
    // Extra protection for email content
    return sanitized
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  } catch (error) {
    console.error('Error sanitizing email content:', error);
    return '';
  }
}

/**
 * Checks if content contains potentially dangerous HTML
 * Useful for validation before saving content
 */
export function containsDangerousHtml(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  const dangerousPatterns = [
    /<script\b/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe\b/i,
    /<object\b/i,
    /<embed\b/i,
    /<form\b/i,
    /data:text\/html/i
  ];
  
  return dangerousPatterns.some(pattern => pattern.test(content));
}